<template>
  <div class="cash-box-page">
    <div class="cash-box-heading">
      <div>
        <div class="cash-box-eyebrow">CONTROL OPERATIVO</div>
        <h1>Cash Box</h1>
        <p>Every cash movement in one continuous studio ledger.</p>
      </div>
      <v-btn
        v-if="canEdit"
        color="primary"
        prepend-icon="mdi-plus"
        @click="openCreateDialog"
      >
        Add Movement
      </v-btn>
    </div>

    <v-alert
      v-if="error"
      class="mb-4"
      type="warning"
      variant="tonal"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>

    <div class="cash-box-summary">
      <div class="cash-summary-card cash-summary-card--balance">
        <span>Current Balance</span>
        <strong>{{ formatMoney(summary.currentBalance) }}</strong>
        <small>Continuous balance</small>
      </div>
      <div class="cash-summary-card">
        <span>Money In · {{ selectedYear }}</span>
        <strong class="positive">{{ formatMoney(summary.moneyIn) }}</strong>
        <small>Cash received</small>
      </div>
      <div class="cash-summary-card">
        <span>Money Out · {{ selectedYear }}</span>
        <strong class="negative">{{ formatMoney(summary.moneyOut) }}</strong>
        <small>Cash delivered or spent</small>
      </div>
      <div class="cash-summary-card">
        <span>Open Adelantos / Prestamos</span>
        <strong>{{ summary.openRecoveries }}</strong>
        <small>Pending Adjustments</small>
      </div>
    </div>

    <section class="cash-ledger-card">
      <div class="cash-ledger-toolbar">
        <div>
          <h2>Cash Movements</h2>
          <p>The balance carries forward; the year only filters the rows below.</p>
        </div>
        <div class="cash-ledger-filters">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            class="year-filter"
            density="compact"
            hide-details
            label="Year"
            variant="outlined"
            @update:modelValue="loadEntries"
          />
          <v-text-field
            v-model="search"
            class="cash-search"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-magnify"
            placeholder="Search person, reason or notes"
            variant="outlined"
            @update:modelValue="scheduleSearch"
          />
        </div>
      </div>

      <div v-if="loading" class="cash-loading">
        <v-progress-circular indeterminate color="primary" size="30" width="3" />
      </div>

      <div v-else-if="entries.length" class="cash-table-wrap">
        <table class="cash-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Person</th>
              <th>Reason</th>
              <th>Movement</th>
              <th class="money-column">Amount</th>
              <th class="money-column">Balance</th>
              <th>Adelanto / Prestamo</th>
              <th>Entered By</th>
              <th>Notes</th>
              <th v-if="canEdit" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id">
              <td class="date-cell">{{ formatDate(entry.entry_date) }}</td>
              <td>
                <strong>{{ entry.person_name }}</strong>
                <small v-if="entry.model_stage_name">{{ entry.model_stage_name }}</small>
              </td>
              <td>{{ entry.reason }}</td>
              <td>
                <v-chip
                  size="small"
                  :color="entry.movement === 'Money In' ? 'success' : 'error'"
                  variant="tonal"
                >
                  {{ entry.movement }}
                </v-chip>
              </td>
              <td :class="['money-cell', entry.movement === 'Money In' ? 'positive' : 'negative']">
                {{ entry.movement === 'Money In' ? '+' : '−' }}{{ formatMoney(entry.amount) }}
              </td>
              <td class="money-cell balance-cell">{{ formatMoney(entry.running_balance) }}</td>
              <td>
                <template v-if="entry.entry_type !== 'General'">
                  <v-chip
                    size="small"
                    :color="recoveryColor(entry)"
                    variant="tonal"
                  >
                    {{ recoveryLabel(entry) }}
                  </v-chip>
                  <small v-if="entry.entry_type === 'Préstamo' && Number(entry.quotas) > 1">
                    {{ entry.quotas }} cuotas
                  </small>
                </template>
              </td>
              <td class="entered-by">{{ enteredBy(entry) }}</td>
              <td class="notes-cell">
                <div class="notes-preview">{{ entry.notes || '—' }}</div>
              </td>
              <td v-if="canEdit" class="actions-cell">
                <v-menu location="bottom end">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-dots-horizontal"
                      size="small"
                      variant="text"
                      aria-label="Cash movement actions"
                    />
                  </template>
                  <v-list density="compact" min-width="170">
                    <v-list-item
                      prepend-icon="mdi-pencil-outline"
                      title="Edit"
                      @click="openEditDialog(entry)"
                    />
                    <v-list-item
                      class="text-error"
                      prepend-icon="mdi-cancel"
                      title="Void Entry"
                      @click="confirmVoid(entry)"
                    />
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="cash-empty">
        <v-icon color="primary" size="34">mdi-cash-register</v-icon>
        <strong>No cash movements in {{ selectedYear }}</strong>
        <p v-if="canEdit">Use Add Movement to enter the current physical cash as your opening balance.</p>
        <p v-else>Cash movements will appear here after a supervisor records them.</p>
      </div>
    </section>

    <v-dialog v-model="dialogOpen" max-width="760" persistent>
      <v-card class="cash-entry-dialog">
        <v-card-title>{{ editingId ? 'Edit Cash Movement' : 'Add Cash Movement' }}</v-card-title>
        <v-card-subtitle>
          Adelantos and Prestamos are automatically sent to Pending Adjustments.
        </v-card-subtitle>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.entry_date"
                density="compact"
                hide-details="auto"
                label="Date"
                type="date"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="form.movement"
                :items="movementOptions"
                :class="['movement-select', form.movement === 'Money In' ? 'movement-select--in' : 'movement-select--out']"
                density="compact"
                hide-details="auto"
                label="Movement"
                variant="outlined"
                @update:modelValue="handleMovementChange"
              >
                <template #item="{ props, item }">
                  <v-list-item
                    v-bind="props"
                    :class="item.value === 'Money In' ? 'movement-option--in' : 'movement-option--out'"
                  />
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.amount"
                autocomplete="off"
                density="compact"
                hide-details="auto"
                inputmode="decimal"
                label="Amount (COP)"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-combobox
                v-model="form.person_name"
                :items="personSuggestions"
                autocomplete="off"
                density="compact"
                hide-details="auto"
                item-title="name"
                item-value="name"
                :return-object="false"
                label="Person / Payee"
                variant="outlined"
                @update:modelValue="handlePersonNameChange"
              >
                <template #item="{ props, item }">
                  <v-list-item v-bind="props">
                    <template #append>
                      <v-btn
                        v-if="item.raw.source === 'history'"
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        aria-label="Remove suggestion"
                        @click.stop="hidePersonSuggestion(item.raw)"
                      />
                    </template>
                  </v-list-item>
                </template>
              </v-combobox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.reason"
                autocomplete="off"
                density="compact"
                hide-details="auto"
                label="Reason"
                :readonly="isRecoverable"
                variant="outlined"
              />
            </v-col>
            <v-col v-if="form.movement === 'Money Out'" cols="12" sm="4">
              <v-select
                v-model="form.entry_type"
                :items="entryTypeOptions"
                density="compact"
                hide-details="auto"
                label="Adelanto / Prestamo"
                variant="outlined"
                @update:modelValue="handleEntryTypeChange"
              />
            </v-col>
            <v-col v-if="isRecoverable" cols="12" :sm="form.entry_type === 'Préstamo' ? 5 : 8">
              <v-autocomplete
                v-model="form.model_id"
                :items="models"
                density="compact"
                hide-details="auto"
                item-title="displayName"
                item-value="id"
                label="Model"
                no-data-text="No matching active model"
                variant="outlined"
                @update:modelValue="applySelectedModel"
              />
            </v-col>
            <v-col v-if="form.entry_type === 'Préstamo'" cols="12" sm="3">
              <v-select
                v-model="form.quotas"
                :items="quotaOptions"
                density="compact"
                hide-details="auto"
                label="Cuotas"
                variant="outlined"
              />
            </v-col>
            <v-col cols="12">
              <v-row dense>
                <v-col cols="6">
                  <v-select
                    v-model="form.tax_document_type"
                    :items="documentOptions"
                    clearable
                    density="compact"
                    hide-details="auto"
                    label="Tax Document (optional)"
                    variant="outlined"
                  />
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="form.receipt_status"
                    :items="receiptOptions"
                    clearable
                    density="compact"
                    hide-details="auto"
                    label="Receipt (optional)"
                    variant="outlined"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.notes"
                auto-grow
                density="compact"
                hide-details="auto"
                label="Notes"
                rows="2"
                variant="outlined"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogOpen = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveEntry">
            {{ editingId ? 'Save Changes' : 'Save Movement' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="voidDialogOpen" max-width="460">
      <v-card>
        <v-card-title>Void Cash Movement?</v-card-title>
        <v-card-text>
          This removes the movement from the running balance. It remains preserved in the database audit history.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="voidDialogOpen = false">Cancel</v-btn>
          <v-btn color="error" :loading="voiding" @click="voidEntry">Void Entry</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import secureAxios from '@/utils/secureAxios';
import { toast } from 'vue3-toastify';

const emptyForm = () => ({
  entry_date: new Date().toISOString().slice(0, 10),
  person_name: '',
  model_id: null,
  reason: '',
  movement: 'Money Out',
  entry_type: 'General',
  amount: '',
  quotas: 1,
  tax_document_type: null,
  receipt_status: null,
  notes: '',
});

export default {
  name: 'CashBox',
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loading: false,
      saving: false,
      voiding: false,
      canEdit: false,
      error: '',
      entries: [],
      models: [],
      personSuggestions: [],
      selectedYear: currentYear,
      search: '',
      searchTimer: null,
      summary: { currentBalance: 0, moneyIn: 0, moneyOut: 0, openRecoveries: 0 },
      dialogOpen: false,
      voidDialogOpen: false,
      editingId: null,
      voidTarget: null,
      form: emptyForm(),
      movementOptions: ['Money In', 'Money Out'],
      entryTypeOptions: [
        { title: 'None', value: 'General' },
        { title: 'Adelanto', value: 'Adelanto' },
        { title: 'Prestamo', value: 'Préstamo' },
      ],
      quotaOptions: [1, 2, 3, 4, 5, 6],
      documentOptions: [
        { title: 'Factura Electrónica', value: 'Factura Electronica' },
        { title: 'Cuenta de Cobro', value: 'Cuenta de Cobro' },
      ],
      receiptOptions: ['Missing', 'Received', 'Printed', 'Emailed', 'Google Drive'],
    };
  },
  computed: {
    isRecoverable() {
      return this.form.entry_type === 'Adelanto' || this.form.entry_type === 'Préstamo';
    },
    yearOptions() {
      const current = new Date().getFullYear();
      return Array.from({ length: 7 }, (_, index) => current + 1 - index);
    },
  },
  async mounted() {
    await Promise.all([this.loadEntries(), this.loadModels(), this.loadPersonSuggestions()]);
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async loadEntries() {
      this.loading = true;
      this.error = '';
      try {
        const response = await secureAxios.get('/api/cash-box', {
          params: {
            year: this.selectedYear,
            search: this.search || undefined,
          },
        });
        this.entries = response.data.entries || [];
        this.summary = response.data.summary || this.summary;
        this.canEdit = response.data.canEdit === true;
      } catch (error) {
        this.error = error?.response?.data?.message || 'Unable to load Cash Box';
      } finally {
        this.loading = false;
      }
    },
    async loadModels() {
      try {
        const response = await secureAxios.get('/api/cash-box/models');
        this.models = (response.data || []).map((model) => ({
          ...model,
          displayName: model.stageName
            ? `${model.fullName || model.stageName} · ${model.stageName}`
            : model.fullName,
        }));
      } catch (error) {
        console.error('Unable to load Cash Box models:', error?.message || error);
      }
    },
    async loadPersonSuggestions() {
      try {
        const response = await secureAxios.get('/api/cash-box/person-suggestions');
        this.personSuggestions = (response.data || []).filter((item) => item?.name);
      } catch (error) {
        console.error('Unable to load Cash Box person suggestions:', error?.message || error);
      }
    },
    async hidePersonSuggestion(item) {
      if (!item?.name || item.source !== 'history') return;
      try {
        await secureAxios.post('/api/cash-box/person-suggestions/hide', { name: item.name });
        this.personSuggestions = this.personSuggestions.filter((suggestion) => suggestion.name !== item.name);
        toast.success('Suggestion removed');
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Unable to remove suggestion');
      }
    },
    scheduleSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadEntries(), 300);
    },
    openCreateDialog() {
      this.editingId = null;
      this.form = emptyForm();
      this.dialogOpen = true;
    },
    openEditDialog(entry) {
      const accountingNotes = this.parseAccountingNotes(entry.notes);
      this.editingId = entry.id;
      this.form = {
        entry_date: this.formatDateValue(entry.entry_date),
        person_name: entry.person_name,
        model_id: entry.model_id ? Number(entry.model_id) : null,
        reason: entry.reason,
        movement: entry.movement,
        entry_type: entry.entry_type,
        amount: Number(entry.amount).toLocaleString('en-US'),
        quotas: Number(entry.quotas || 1),
        tax_document_type: accountingNotes.taxDocumentType,
        receipt_status: accountingNotes.receiptStatus,
        notes: accountingNotes.notes,
      };
      this.dialogOpen = true;
    },
    handleMovementChange(movement) {
      if (movement === 'Money In') {
        this.form.entry_type = 'General';
        this.form.model_id = null;
        this.form.quotas = 1;
      }
    },
    handleEntryTypeChange(type) {
      if (type !== 'Préstamo') this.form.quotas = 1;
      if (type === 'General') {
        this.form.model_id = null;
        if (['Adelanto de Pago', 'Prestamo', 'Préstamo'].includes(this.form.reason)) this.form.reason = '';
        return;
      }
      this.form.reason = type === 'Adelanto' ? 'Adelanto de Pago' : 'Prestamo';
    },
    handlePersonNameChange(name) {
      if (!this.isRecoverable || !name) return;
      const normalizedName = String(name).trim().toLocaleLowerCase();
      const model = this.models.find((candidate) => (
        String(candidate.fullName || '').trim().toLocaleLowerCase() === normalizedName
        || String(candidate.stageName || '').trim().toLocaleLowerCase() === normalizedName
      ));
      if (model) this.form.model_id = Number(model.id);
    },
    applySelectedModel(modelId) {
      const model = this.models.find((candidate) => Number(candidate.id) === Number(modelId));
      if (model) this.form.person_name = model.fullName || model.stageName || this.form.person_name;
    },
    parseAmount(value) {
      const parsed = Number(String(value || '').replace(/,/g, '').trim());
      return Number.isFinite(parsed) ? parsed : 0;
    },
    parseAccountingNotes(value) {
      let taxDocumentType = null;
      let receiptStatus = null;
      const remainingLines = [];
      String(value || '').split(/\r?\n/).forEach((line) => {
        const taxMatch = line.match(/^Tax Document:\s*(.+)$/i);
        const receiptMatch = line.match(/^Receipt:\s*(.+)$/i);
        if (taxMatch) taxDocumentType = taxMatch[1].trim();
        else if (receiptMatch) receiptStatus = receiptMatch[1].trim();
        else remainingLines.push(line);
      });
      return {
        taxDocumentType,
        receiptStatus,
        notes: remainingLines.join('\n').trim(),
      };
    },
    composeAccountingNotes() {
      return [
        this.form.tax_document_type ? `Tax Document: ${this.form.tax_document_type}` : '',
        this.form.receipt_status ? `Receipt: ${this.form.receipt_status}` : '',
        String(this.form.notes || '').trim(),
      ].filter(Boolean).join('\n');
    },
    async saveEntry() {
      const payload = {
        ...this.form,
        amount: this.parseAmount(this.form.amount),
        quotas: this.form.entry_type === 'Préstamo' ? Number(this.form.quotas) : 1,
        notes: this.composeAccountingNotes(),
      };
      delete payload.tax_document_type;
      delete payload.receipt_status;
      if (!payload.entry_date || !payload.person_name.trim() || !payload.reason.trim() || payload.amount <= 0) {
        toast.error('Date, person, reason, and a positive amount are required');
        return;
      }
      if (this.isRecoverable && !payload.model_id) {
        toast.error(`Select the model receiving the ${payload.entry_type}`);
        return;
      }

      this.saving = true;
      try {
        const response = this.editingId
          ? await secureAxios.put(`/api/cash-box/${this.editingId}`, payload)
          : await secureAxios.post('/api/cash-box', payload);
        toast.success(response.data?.message || 'Cash Box entry saved');
        this.dialogOpen = false;
        await this.loadEntries();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Unable to save Cash Box entry');
      } finally {
        this.saving = false;
      }
    },
    confirmVoid(entry) {
      this.voidTarget = entry;
      this.voidDialogOpen = true;
    },
    async voidEntry() {
      if (!this.voidTarget) return;
      this.voiding = true;
      try {
        const response = await secureAxios.delete(`/api/cash-box/${this.voidTarget.id}`);
        toast.success(response.data?.message || 'Cash Box entry voided');
        this.voidDialogOpen = false;
        this.voidTarget = null;
        await this.loadEntries();
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Unable to void Cash Box entry');
      } finally {
        this.voiding = false;
      }
    },
    recoveryLabel(entry) {
      if (entry.entry_type === 'General') return 'None';
      const labels = {
        pending_review: 'Pending Review',
        in_adjustments: 'In Adjustments',
        needs_review: 'Needs Review',
        recovered: 'Completed',
      };
      const type = entry.entry_type === 'Préstamo' ? 'Prestamo' : entry.entry_type;
      return `${type} · ${labels[entry.recovery_status] || 'Pending Review'}`;
    },
    recoveryColor(entry) {
      if (entry.entry_type === 'General') return 'grey';
      if (entry.recovery_status === 'recovered') return 'success';
      if (entry.recovery_status === 'needs_review') return 'error';
      if (entry.recovery_status === 'in_adjustments') return 'primary';
      return 'warning';
    },
    enteredBy(entry) {
      const value = entry.entered_by_name || entry.updated_by_email || entry.entered_by_email || '';
      if (!value) return '—';
      const username = String(value).trim().split('@')[0];
      return ({ jono: 'Jono', 'carolina.r': 'Carolina', 'jorge.n': 'Jorge' })[username.toLowerCase()] || username;
    },
    formatMoney(value) {
      return `$ ${Math.round(Number(value || 0)).toLocaleString('en-US')}`;
    },
    formatDateValue(value) {
      return String(value || '').slice(0, 10);
    },
    formatDate(value) {
      const text = this.formatDateValue(value);
      if (!text) return '—';
      return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
        .format(new Date(`${text}T00:00:00Z`));
    },
  },
};
</script>

<style scoped>
.cash-box-page {
  max-width: 1420px;
  margin: 0 auto;
  padding: 28px 28px 60px;
  color: #203247;
}

.cash-box-heading,
.cash-ledger-toolbar,
.cash-ledger-filters {
  display: flex;
  align-items: center;
}

.cash-box-heading {
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.cash-box-eyebrow {
  margin-bottom: 3px;
  color: #6b7f93;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.cash-box-page h1 {
  margin: 0;
  color: #0a2647;
  font-size: 30px;
  line-height: 1.2;
}

.cash-box-page p {
  margin: 4px 0 0;
  color: #6b7f93;
  font-size: 13px;
}

.cash-box-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.cash-summary-card {
  min-height: 102px;
  padding: 16px 18px;
  border: 1px solid #dfe7f0;
  border-radius: 9px;
  background: #fff;
}

.cash-summary-card--balance {
  border-left: 4px solid #2c74b3;
}

.cash-summary-card span,
.cash-summary-card small {
  display: block;
  color: #6b7f93;
  font-size: 12px;
}

.cash-summary-card strong {
  display: block;
  margin: 5px 0 2px;
  color: #152c42;
  font-size: 23px;
}

.positive { color: #208653 !important; }
.negative { color: #b74a4a !important; }

.cash-ledger-card {
  overflow: hidden;
  border: 1px solid #dfe7f0;
  border-radius: 9px;
  background: #fff;
}

.cash-ledger-toolbar {
  justify-content: space-between;
  gap: 20px;
  min-height: 76px;
  padding: 14px 16px;
  border-bottom: 1px solid #dfe7f0;
}

.cash-ledger-toolbar h2 {
  margin: 0;
  color: #142b3f;
  font-size: 18px;
}

.cash-ledger-filters { gap: 10px; }
.year-filter { width: 125px; }
.cash-search { width: 285px; }

.cash-table-wrap { overflow-x: auto; }
.cash-table {
  width: 100%;
  min-width: 1260px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}

.cash-table th {
  padding: 9px 10px;
  background: #434a51;
  color: #fff;
  font-size: 10px;
  font-weight: 750;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
}

.cash-table td {
  height: 51px;
  padding: 7px 10px;
  border-bottom: 1px solid #e4e9ee;
  vertical-align: middle;
}

.cash-table tbody tr:nth-child(even) td { background: #f2f6fa; }
.cash-table tbody tr:hover td { background: #eaf3fb; }
.cash-table td strong,
.cash-table td small { display: block; }
.cash-table td small { margin-top: 2px; color: #718296; font-size: 10px; }

.date-cell { white-space: nowrap; }
.money-column,
.money-cell { text-align: right !important; white-space: nowrap; }
.money-cell { font-weight: 700; }
.balance-cell { color: #21384e; }
.entered-by { max-width: 110px; overflow: hidden; text-overflow: ellipsis; }
.notes-cell {
  width: 230px;
  max-width: 230px;
  color: #52677b;
}

.notes-preview {
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  line-height: 15px;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.actions-cell { width: 48px; padding-left: 3px !important; padding-right: 3px !important; text-align: center; }

.cash-loading,
.cash-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 260px;
}

.cash-empty {
  flex-direction: column;
  text-align: center;
}

.cash-empty strong { margin-top: 9px; color: #203247; }
.cash-entry-dialog :deep(.v-card-title) { padding-bottom: 4px; }
.movement-select--in :deep(.v-field__input),
.movement-option--in { color: #208653 !important; font-weight: 700; }
.movement-select--out :deep(.v-field__input),
.movement-option--out { color: #b42318 !important; font-weight: 700; }

@media (max-width: 900px) {
  .cash-box-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .cash-ledger-toolbar { align-items: flex-start; flex-direction: column; }
  .cash-ledger-filters { width: 100%; }
  .cash-search { flex: 1; width: auto; }
}

@media (max-width: 600px) {
  .cash-box-page { padding: 20px 14px 45px; }
  .cash-box-heading { align-items: flex-start; flex-direction: column; }
  .cash-box-summary { grid-template-columns: 1fr; }
  .cash-ledger-filters { align-items: stretch; flex-direction: column; }
  .year-filter,
  .cash-search { width: 100%; }
}
</style>
